import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {
  socialApi,
  socialFieldErrors,
  type CreateSocialTargetInput,
  type SocialActionResponse,
  type SocialPlatform,
  type SocialTarget,
} from '@/api/social'
import { targetLabel } from '@/components/admin/social/socialUi'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

const FORM_SX = { display: 'flex', flexDirection: 'column', minHeight: 0 } as const

/** Поле адреса цели: chat_id у Telegram, group_id у VK */
function addressField(platform: SocialPlatform): 'chat_id' | 'group_id' {
  return platform === 'telegram' ? 'chat_id' : 'group_id'
}

function SubmitIcon({ submitting }: { submitting: boolean }) {
  return submitting ? <CircularProgress size={16} color="inherit" /> : null
}

interface CreateDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (response: SocialActionResponse<SocialTarget>) => void
}

/** «Добавить канал»: платформа, ключ и адрес. Токен задаётся отдельно. */
export function SocialTargetCreateDialog({ open, onClose, onCreated }: CreateDialogProps) {
  const { t } = useTranslation()
  const [platform, setPlatform] = useState<SocialPlatform>('telegram')
  const [key, setKey] = useState('')
  const [address, setAddress] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setPlatform('telegram')
    setKey('')
    setAddress('')
    setFieldErrors({})
    setError(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const field = addressField(platform)
    const errors: Record<string, string> = {}
    if (key.trim() === '') errors.key = t('admin.social.targetDialog.required')
    if (address.trim() === '') {
      errors[field] = t('admin.social.targetDialog.required')
    } else if (platform === 'vk' && !/^\d+$/.test(address.trim())) {
      errors.group_id = t('admin.social.targetDialog.groupIdHelp')
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }

    const body: CreateSocialTargetInput =
      platform === 'telegram'
        ? { platform, key: key.trim(), chat_id: address.trim() }
        : { platform, key: key.trim(), group_id: Number(address.trim()) }

    setSubmitting(true)
    setError(null)
    setFieldErrors({})
    try {
      onCreated(await socialApi.createTarget(body))
    } catch (err) {
      setFieldErrors(socialFieldErrors(err))
      setError(displayErrorMessage(getErrorMessage(err), t('admin.social.actionFailed')))
    } finally {
      setSubmitting(false)
    }
  }

  const field = addressField(platform)

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onClose()
      }}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onExited: reset }}
    >
      <Box component="form" onSubmit={handleSubmit} noValidate sx={FORM_SX}>
        <DialogTitle>{t('admin.social.targetDialog.createTitle')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.social.targetDialog.createHint')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="social-target-platform">{t('admin.social.targetDialog.platform')}</InputLabel>
              <Select
                labelId="social-target-platform"
                label={t('admin.social.targetDialog.platform')}
                value={platform}
                onChange={(event) => {
                  setPlatform(event.target.value as SocialPlatform)
                  setAddress('')
                  setFieldErrors({})
                }}
              >
                <MenuItem value="telegram">{t('admin.social.platforms.telegram')}</MenuItem>
                <MenuItem value="vk">{t('admin.social.platforms.vk')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              label={t('admin.social.targetDialog.key')}
              value={key}
              onChange={(event) => setKey(event.target.value)}
              error={Boolean(fieldErrors.key)}
              helperText={fieldErrors.key ?? t('admin.social.targetDialog.keyHelp')}
              slotProps={{ htmlInput: { maxLength: 64, autoCapitalize: 'off', spellCheck: false } }}
            />
            <TextField
              size="small"
              fullWidth
              label={t(platform === 'telegram' ? 'admin.social.targetDialog.chatId' : 'admin.social.targetDialog.groupId')}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              error={Boolean(fieldErrors[field])}
              helperText={
                fieldErrors[field] ??
                t(platform === 'telegram' ? 'admin.social.targetDialog.chatIdHelp' : 'admin.social.targetDialog.groupIdHelp')
              }
              slotProps={{
                htmlInput: {
                  maxLength: 64,
                  inputMode: platform === 'vk' ? 'numeric' : 'text',
                  autoCapitalize: 'off',
                  spellCheck: false,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={submitting} startIcon={<SubmitIcon submitting={submitting} />}>
            {t('common.create')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

interface EditDialogProps {
  open: boolean
  target: SocialTarget | null
  onClose: () => void
  onSaved: (response: SocialActionResponse<SocialTarget>) => void
}

/** «Изменить»: адрес выключенной цели — chat_id или group_id */
export function SocialTargetEditDialog({ open, target, onClose, onSaved }: EditDialogProps) {
  const { t } = useTranslation()
  const [address, setAddress] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open || !target) return

    setAddress(target.platform === 'telegram' ? (target.chat_id ?? '') : target.group_id ? String(target.group_id) : '')
    setFieldError(null)
    setError(null)
  }, [open, target])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!target) return

    const value = address.trim()
    if (value === '') {
      setFieldError(t('admin.social.targetDialog.required'))
      return
    }
    if (target.platform === 'vk' && !/^\d+$/.test(value)) {
      setFieldError(t('admin.social.targetDialog.groupIdHelp'))
      return
    }

    setSubmitting(true)
    setError(null)
    setFieldError(null)
    try {
      onSaved(
        await socialApi.updateTarget(
          target.id,
          target.platform === 'telegram' ? { chat_id: value } : { group_id: Number(value) },
        ),
      )
    } catch (err) {
      setFieldError(socialFieldErrors(err)[addressField(target.platform)] ?? null)
      setError(displayErrorMessage(getErrorMessage(err), t('admin.social.actionFailed')))
    } finally {
      setSubmitting(false)
    }
  }

  const isTelegram = target?.platform !== 'vk'

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onClose()
      }}
      maxWidth="xs"
      fullWidth
    >
      <Box component="form" onSubmit={handleSubmit} noValidate sx={FORM_SX}>
        <DialogTitle sx={{ overflowWrap: 'anywhere' }}>
          {t('admin.social.targetDialog.editTitle', { target: target ? targetLabel(target, target.id) : '' })}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.social.targetDialog.editHint')}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={t(isTelegram ? 'admin.social.targetDialog.chatId' : 'admin.social.targetDialog.groupId')}
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            error={fieldError !== null}
            helperText={
              fieldError ?? t(isTelegram ? 'admin.social.targetDialog.chatIdHelp' : 'admin.social.targetDialog.groupIdHelp')
            }
            slotProps={{
              htmlInput: { maxLength: 64, inputMode: isTelegram ? 'text' : 'numeric', autoCapitalize: 'off', spellCheck: false },
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={submitting || !target} startIcon={<SubmitIcon submitting={submitting} />}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

interface TokenDialogProps {
  open: boolean
  target: SocialTarget | null
  onClose: () => void
  onSaved: (response: SocialActionResponse<SocialTarget>) => void
}

/**
 * «Задать токен». Значение живёт только в локальном состоянии диалога: не в
 * сторе, не в localStorage, не в URL и не в console. Поле пустое при каждом
 * открытии и стирается после сохранения и при закрытии; текущая маска — в
 * подсказке поля.
 */
export function SocialTokenDialog({ open, target, onClose, onSaved }: TokenDialogProps) {
  const { t } = useTranslation()
  const [token, setToken] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setToken('')
      setError(null)
    }
  }, [open, target?.id])

  const handleClose = () => {
    if (submitting) return
    setToken('')
    onClose()
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!target) return

    const value = token.trim()
    if (value === '') {
      setError(t('admin.social.targetDialog.required'))
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const response = await socialApi.storeCredentials(target.id, value)
      setToken('')
      onSaved(response)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('admin.social.actionFailed')))
    } finally {
      setSubmitting(false)
    }
  }

  const helperText =
    target?.has_credentials && target.credentials_mask
      ? t('admin.social.tokenDialog.currentMask', { mask: target.credentials_mask })
      : t('admin.social.tokenDialog.noToken')

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setToken('') }}
    >
      <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off" sx={FORM_SX}>
        <DialogTitle sx={{ overflowWrap: 'anywhere' }}>
          {t('admin.social.tokenDialog.title', { target: target ? targetLabel(target, target.id) : '' })}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(target?.platform === 'vk' ? 'admin.social.tokenDialog.hintVk' : 'admin.social.tokenDialog.hintTelegram')}
            {'. '}
            {t('admin.social.tokenDialog.storageHint')}
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="password"
            name="social-target-token"
            autoComplete="new-password"
            label={t('admin.social.tokenDialog.label')}
            value={token}
            onChange={(event) => setToken(event.target.value)}
            helperText={helperText}
            slotProps={{ htmlInput: { maxLength: 512, autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={submitting || !target} startIcon={<SubmitIcon submitting={submitting} />}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
